import { motion, useScroll, useTransform } from 'motion/react';
import { useRef } from 'react';
import { useInView } from '../../../components/hooks/useInView.jsx';
import { TrendingUp, AlertCircle, Target, Award } from 'lucide-react';

export function CompetitiveAnalysis() {
  const [ref, isInView] = useInView({ threshold: 0.1 });
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const z = useTransform(y, (val) => -val);

  const competitors = [
    {
      name: 'My Fitness App',
      label: 'Concept',
      strengths: [
        'Comprehensive fitness, diet, and sleep tracking',
        'Personalized plans for all users',
        'Intuitive user interface'
      ],
      weaknesses: [
        'New to market',
        'No real user data yet'
      ],
      opportunities: [
        'Social engagement features',
        'AI-driven insights'
      ],
      usp: 'Holistic tracking and personalization in one platform',
      isOurs: true
    },
    {
      name: 'MyFitnessPal',
      label: 'Leader',
      strengths: [
        'Extensive food database',
        'Strong community features',
        'Wearable device syncing'
      ],
      weaknesses: [
        'Limited free version features',
        'Outdated UI design'
      ],
      opportunities: [
        'Personalized meal recommendations',
        'Mental wellness integrations'
      ],
      usp: 'Strong nutrition database and calorie management',
      isOurs: false
    },
    {
      name: 'Nike Training Club',
      label: 'Premium',
      strengths: [
        'High-quality workout videos',
        'Wide training categories',
        'Professional guided sessions'
      ],
      weaknesses: [
        'Limited nutrition guidance',
        'Focuses only on workouts'
      ],
      opportunities: [
        'Add gamification elements',
        'Partner with nutrition services'
      ],
      usp: 'Professional coaching and guided workouts by Nike trainers',
      isOurs: false
    },
    {
      name: 'Fitbit App',
      label: 'Analytics',
      strengths: [
        'Detailed health analytics',
        'Sleep and heart rate insights',
        'Community challenges'
      ],
      weaknesses: [
        'Heavy device dependency',
        'Expensive subscription model'
      ],
      opportunities: [
        'Enhanced app independence',
        'Mental health tracking'
      ],
      usp: 'Strong real-time integration with wearables',
      isOurs: false
    }
  ];

  const categories = [
    { title: 'Strengths', icon: TrendingUp, color: 'text-green-400' },
    { title: 'Weaknesses', icon: AlertCircle, color: 'text-red-400' },
    { title: 'Opportunities', icon: Target, color: 'text-blue-400' },
    { title: 'USP', icon: Award, color: 'text-[#fd5b38]' }
  ];

  return (
    <section ref={containerRef} className="py-20 px-6 bg-gradient-to-b from-black/30 via-transparent to-black/30 overflow-hidden">
      <div ref={ref} className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl mb-8">
            Competitive Analysis
          </h2>
          <div className="h-1 w-20 bg-[#fd5b38] mx-auto mb-8" />
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto">
            Understanding the competitive landscape to identify opportunities and position our solution
          </p>
        </motion.div>

        {/* Mobile View - Cards */}
        <div className="lg:hidden space-y-6">
          {competitors.map((competitor, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className={`bg-[#1a1a1a] rounded-2xl item-border ${
                competitor.isOurs ? 'border-[#fd5b38]' : 'border-gray-800'
              } hover:border-[#fd5b38] transition-all duration-300 overflow-hidden`}
            >
              <div className={`p-6 ${competitor.isOurs ? 'bg-gradient-to-r from-[#fd5b38]/20 to-transparent' : ''}`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-2xl">{competitor.name}</h3>
                  <span className={`text-sm px-3 py-1 rounded-full ${
                    competitor.isOurs 
                      ? 'bg-[#fd5b38] text-white' 
                      : 'bg-gray-800 text-gray-400'
                  }`}>
                    {competitor.label}
                  </span>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    <h4 className="text-lg text-green-400">Strengths</h4>
                  </div>
                  <ul className="space-y-2">
                    {competitor.strengths.map((strength, i) => (
                      <li key={i} className="text-gray-400 text-sm flex items-start gap-2">
                        <span className="text-green-400 mt-1">+</span>
                        <span>{strength}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    <h4 className="text-lg text-red-400">Weaknesses</h4>
                  </div>
                  <ul className="space-y-2">
                    {competitor.weaknesses.map((weakness, i) => (
                      <li key={i} className="text-gray-400 text-sm flex items-start gap-2">
                        <span className="text-red-400 mt-1">-</span>
                        <span>{weakness}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-5 h-5 text-blue-400" />
                    <h4 className="text-lg text-blue-400">Opportunities</h4>
                  </div>
                  <ul className="space-y-2">
                    {competitor.opportunities.map((opportunity, i) => (
                      <li key={i} className="text-gray-400 text-sm flex items-start gap-2">
                        <span className="text-blue-400 mt-1">→</span>
                        <span>{opportunity}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-4 border-t border-gray-800">
                  <div className="flex items-center gap-2 mb-3">
                    <Award className="w-5 h-5 text-[#fd5b38]" />
                    <h4 className="text-lg text-[#fd5b38]">Unique Selling Proposition</h4>
                  </div>
                  <p className="text-gray-300">{competitor.usp}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Desktop View - Table */}
        <div className="hidden lg:block overflow-x-auto">
          <div className="min-w-[1000px]">
            {/* Sticky Header */}
            <div className="sticky top-0 z-20 bg-[#0d0d0d]/95 backdrop-blur-lg border-b border-gray-800 pb-4 mb-8">
              <div className="grid grid-cols-5 gap-4">
                <div className="col-span-1" />
                {competitors.map((competitor, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: -20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className={`text-center p-4 rounded-t-2xl ${
                      competitor.isOurs 
                        ? 'bg-gradient-to-b from-[#fd5b38]/20 to-transparent border-[#fd5b38]' 
                        : 'bg-[#1a1a1a]'
                    } border-x border-t ${competitor.isOurs ? 'border-[#fd5b38]' : 'border-gray-800'}`}
                  >
                    <h3 className="text-xl mb-1">{competitor.name}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      competitor.isOurs 
                        ? 'bg-[#fd5b38] text-white' 
                        : 'bg-gray-800 text-gray-400'
                    }`}>
                      {competitor.label}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Table Rows */}
            <div className="space-y-6">
              {categories.map((category, catIndex) => (
                <motion.div
                  key={catIndex}
                  initial={{ opacity: 0, x: -30 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.4 + catIndex * 0.15 }}
                  style={{ y: catIndex % 2 === 0 ? y : z }}
                  className="grid grid-cols-5 gap-4 items-start"
                >
                  {/* Category Label */}
                  <div className="col-span-1 sticky left-0 bg-[#0d0d0d] p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center`}>
                        <category.icon className={`w-5 h-5 ${category.color}`} />
                      </div>
                      <h4 className={`${category.color}`}>{category.title}</h4>
                    </div>
                  </div>

                  {/* Competitor Columns */}
                  {competitors.map((competitor, compIndex) => {
                    let content;
                    if (category.title === 'Strengths') content = competitor.strengths;
                    else if (category.title === 'Weaknesses') content = competitor.weaknesses;
                    else if (category.title === 'Opportunities') content = competitor.opportunities;
                    else content = [competitor.usp];

                    return (
                      <motion.div
                        key={compIndex}
                        whileHover={{ 
                          scale: 1.02,
                          boxShadow: competitor.isOurs 
                            ? '0 0 30px rgba(253, 91, 56, 0.4)' 
                            : '0 0 20px rgba(253, 91, 56, 0.2)'
                        }}
                        className={`p-6 rounded-2xl item-border transition-all duration-300 ${
                          competitor.isOurs 
                            ? 'bg-gradient-to-br from-[#1a1a1a] to-black border-[#fd5b38]/50 hover:border-[#fd5b38]' 
                            : 'bg-[#1a1a1a] border-gray-800 hover:border-[#fd5b38]/50'
                        } group relative overflow-hidden`}
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-[#fd5b38]/0 to-[#fd5b38]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        
                        <div className="relative z-10">
                          {category.title === 'USP' ? (
                            <p className="text-gray-300 leading-relaxed">{content[0]}</p>
                          ) : (
                            <ul className="space-y-2">
                              {content.map((item, itemIndex) => (
                                <li key={itemIndex} className="text-gray-400 text-sm flex items-start gap-2">
                                  <span className={`${category.color} mt-1 flex-shrink-0`}>
                                    {category.title === 'Strengths' && '+'}
                                    {category.title === 'Weaknesses' && '-'}
                                    {category.title === 'Opportunities' && '→'}
                                  </span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Key Insights */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-16 bg-gradient-to-r from-[#fd5b38]/10 via-transparent to-[#fd5b38]/10 p-8 rounded-3xl item-border border-[#fd5b38]/30"
        >
          <h3 className="text-3xl md:text-4xl mb-6 text-center text-[#fd5b38]">
            Competitive Positioning
          </h3>
          <p className="text-xl text-gray-300 text-center max-w-4xl mx-auto leading-relaxed">
            Our concept fills the gap by offering a <span className="text-[#fd5b38]">holistic, all-in-one solution</span> that 
            integrates fitness, nutrition, and sleep tracking with personalized insights. Unlike competitors who focus on 
            single aspects, we provide a <span className="text-[#fd5b38]">unified wellness experience</span> that adapts to 
            each user's unique journey, combining the best features from across the market into one intuitive platform.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
